import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { GameDetailsModel, mapHostedGamesItemDto, mapPlayerGameDto, mapPlayerGameInvitationDto } from '@core';
import { GetMyGamesResponse, HostedGameItemDto, PlayerGameDto, PlayerGameInvitationDto } from '@core-http';
import { RedirectTypeEnum } from '@ui-components';
import { MyGamesStorage } from '../../../../infrastructure';
import { MyGameItem, MyGamesSource } from '../../../models';
import { filterFuture, groupMyGamesByMonth, sortMyGamesDesc } from '../../../utils';

export class GetMyGamesQueryHandler {
  private readonly myGamesStorage: MyGamesStorage = inject(MyGamesStorage);

  isLoaded$(): Observable<boolean> {
    return this.myGamesStorage.loaded$;
  }

  getGroupedByMonth(source: MyGamesSource): Observable<Map<string, MyGameItem[]>> {
    return this.myGamesStorage.games$.pipe(
      map((dto: GetMyGamesResponse) => this.selectAndMap(dto, source)),
      map((items: MyGameItem[]) => groupMyGamesByMonth(items))
    );
  }

  getFutureHosted$(): Observable<GameDetailsModel[]> {
    return this.myGamesStorage.games$.pipe(
      map((dto: GetMyGamesResponse) =>
        filterFuture(dto.host).map((game: HostedGameItemDto) => mapHostedGamesItemDto(game))
      )
    );
  }

  getFuturePlayerApproved$(): Observable<GameDetailsModel[]> {
    return this.myGamesStorage.games$.pipe(
      map((dto: GetMyGamesResponse) =>
        filterFuture(dto.player.approved).map((game: PlayerGameDto) => mapPlayerGameDto(game))
      )
    );
  }

  getFuturePlayerWaiting$(): Observable<GameDetailsModel[]> {
    return this.myGamesStorage.games$.pipe(
      map((dto: GetMyGamesResponse) =>
        filterFuture(dto.player.waiting).map((game: PlayerGameDto) => mapPlayerGameDto(game))
      )
    );
  }

  getInvitations$(): Observable<GameDetailsModel[]> {
    return this.myGamesStorage.games$.pipe(
      map((dto: GetMyGamesResponse) =>
        dto.player.invitations.map((game: PlayerGameInvitationDto) => mapPlayerGameInvitationDto(game))
      )
    );
  }

  private selectAndMap(response: GetMyGamesResponse, source: MyGamesSource): MyGameItem[] {
    const host: MyGameItem[] = response.host.map((game: HostedGameItemDto) => ({
      ...mapHostedGamesItemDto(game),
      redirectType: RedirectTypeEnum.HOSTED
    }));

    const playerApproved: MyGameItem[] = response.player.approved.map((game: PlayerGameDto) => ({
      ...mapPlayerGameDto(game),
      redirectType: RedirectTypeEnum.GAME
    }));
    const playerWaiting: MyGameItem[] = response.player.waiting.map((game: PlayerGameDto) => ({
      ...mapPlayerGameDto(game),
      redirectType: RedirectTypeEnum.GAME
    }));
    const playerInvitations: MyGameItem[] = response.player.invitations.map((game: PlayerGameInvitationDto) => ({
      ...mapPlayerGameInvitationDto(game),
      redirectType: RedirectTypeEnum.GAME
    }));
    const player: MyGameItem[] = [...playerApproved, ...playerWaiting, ...playerInvitations];

    switch (source) {
      case MyGamesSource.HOSTED:
        return host;
      case MyGamesSource.PLAYER:
        return sortMyGamesDesc(player);
      case MyGamesSource.ALL:
      default:
        return this.mergeDedupHostWins(host, player);
    }
  }

  private mergeDedupHostWins(host: MyGameItem[], player: MyGameItem[]): MyGameItem[] {
    const hostIds: Set<string> = new Set(host.map((game: MyGameItem) => game.gameId));
    const playerOnly: MyGameItem[] = player.filter((game: MyGameItem) => !hostIds.has(game.gameId));
    return sortMyGamesDesc([...host, ...playerOnly]);
  }
}
